import React from 'react';
import styled from 'styled-components';
import {
  screenXsMin,
  screenSmMin,
  screenMdMin,
} from '@spotify-internal/encore-web';

import { COLOR_BLACK } from 'client/components/MarketingPagesShared/styles';
import { useT } from 'client/i18n/hooks/useT';
import { CreateIcon, UploadIcon, WatchIcon } from './svgs';

type ProcessStepProps = {
  body: string;
  icon: JSX.Element;
  title: string;
};

const ProcessSection = styled.section`
  padding: 120px 50px 60px;

  @media (max-width: ${screenMdMin}) {
    padding: 0 50px 60px;
  }

  @media (max-width: ${screenSmMin}) {
    padding: 0 5% 60px;
  }
`;

const ProcessHeader = styled.h2`
  font-size: 3.125rem;
  line-height: 40px;
  text-align: center;
  margin: 0 0 50px;
  color: ${COLOR_BLACK};

  @media (max-width: ${screenXsMin}) {
    font-size: 2rem;
  }
`;

const ProcessStepsContainer = styled.div`
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  margin: 0 auto 40px;
  width: 100%;
  max-width: 985px;

  @media (max-width: ${screenXsMin}) {
    flex-direction: column;
  }
`;

const ProcessStepContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 30%;
  svg {
    max-height: 125px;
    margin-bottom: 25px;
  }

  @media (max-width: ${screenSmMin}) {
    width: 25%;
  }

  @media (max-width: ${screenXsMin}) {
    margin-bottom: 80px;
    width: 100%;

    &:last-of-type {
      margin-bottom: 0;
    }
  }
`;

const ProcessTitle = styled.h3`
  font-size: 1.375rem;
  line-height: 30px;
  margin: 0 0 16px;
  text-align: center;
  color: ${COLOR_BLACK};

  @media (max-width: ${screenXsMin}) {
    margin-bottom: 12px;
  }
`;

const ProcessBody = styled.p`
  font-size: 1;
  line-height: 20px;
  text-align: center;
  color: ${COLOR_BLACK};
  margin: 0;
`;

function ProcessStep({ body, icon, title }: ProcessStepProps) {
  return (
    <ProcessStepContainer>
      {icon}
      <ProcessTitle>{title}</ProcessTitle>
      <ProcessBody>{body}</ProcessBody>
    </ProcessStepContainer>
  );
}

export function Process() {
  const t = useT();
  const steps: ProcessStepProps[] = [
    {
      icon: <CreateIcon key="integrationConnectionScreenCreateIcon" />,
      title: t(
        'IntegrationConnectionScreen.Process.Title.Create',
        'Create an account',
        'The title for the create step in the integration screen process'
      ),
      body: t(
        'IntegrationConnectionScreen.Process.Body.Create',
        'Use Anchor to store and manage your content',
        'The body for the create step in the integration screen process'
      ),
    },
    {
      icon: <UploadIcon key="integrationConnectionScreenUploadIcon" />,
      title: t(
        'IntegrationConnectionScreen.Process.Title.Upload',
        'Upload to Anchor',
        'The title for the upload step in the integration screen process'
      ),
      body: t(
        'IntegrationConnectionScreen.Process.Body.Upload',
        'Quickly import your video files',
        'The body for the upload step in the integration screen process'
      ),
    },
    {
      icon: <WatchIcon key="integrationConnectionScreenWatchIcon" />,
      title: t(
        'IntegrationConnectionScreen.Process.Title.Watch',
        'Watch or listen on Spotify',
        'The title for the watch step in the integration screen process'
      ),
      body: t(
        'IntegrationConnectionScreen.Process.Body.Watch',
        'Find a new audience among Spotify’s 1 billion listeners',
        'The body for the watch step in the integration screen process'
      ),
    },
  ];

  return (
    <ProcessSection>
      <ProcessHeader>
        {t(
          'IntegrationConnectionScreen.Process.Title',
          'How it works',
          'The title for the process in the integration screen process'
        )}
      </ProcessHeader>
      <ProcessStepsContainer>
        {steps.map(({ icon, title, body }) => (
          <ProcessStep body={body} icon={icon} title={title} key={title} />
        ))}
      </ProcessStepsContainer>
    </ProcessSection>
  );
}
